// ModalComponent.js
import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import styled from "styled-components";
import Button from "@mui/material/Button";
import { gql } from "@apollo/client";
import { client } from "../../../client";
import { uploadFileWithPresignedUrl } from "../../../utils/s3Uploader";
import FileUploader from "../admin-document/FileUploader";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

const ModalContainer = styled(Box)`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  max-width: 600px;
  max-height: 90vh;
  background-color: white;
  padding: 24px;
  z-index: 100;
  border: 1px solid #ccc;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
`;

const FormRow = styled(Box)`
  margin-bottom: 16px;
`;

const FilePreviewContainer = styled(Box)`
  margin-top: 8px;
`;

const BookModal = ({ isOpen, data, handleCloseClick, refetchParentData }) => {
  const [editableData, setEditableData] = useState({});
  const [selectedFile, setSelectedFile] = useState(null); // 썸네일
  const [selectedSampleFile, setSelectedSampleFile] = useState(null); // 샘플 파일
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (data) {
      setEditableData({ ...data });
    }
  }, [data]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;
    if (name === "is_view" || name === "view_order") {
      updatedValue = value ? parseInt(value, 10) : null;
    }
    setEditableData((prev) => ({ ...prev, [name]: updatedValue }));
  };

  const handleRemoveFile = (field) => {
    if (window.confirm("첨부된 파일을 제거하시겠습니까?")) {
      setEditableData((prev) => ({ ...prev, [field]: null }));
      if (field === "object_url") {
        setSelectedFile(null);
      } else if (field === "sample_url") {
        setSelectedSampleFile(null);
      }
    }
  };

  const handleSave = async () => {
    if (!editableData.id) {
      alert("데이터 ID가 없어 수정할 수 없습니다.");
      return;
    }
    setIsLoading(true);
    try {
      const uploadPromises = [];
      // 썸네일 업로드 처리
      if (selectedFile) {
        uploadPromises.push(uploadFileWithPresignedUrl(client, selectedFile));
      } else {
        uploadPromises.push(Promise.resolve(editableData.object_url));
      }
      // 샘플 파일 업로드 처리
      if (selectedSampleFile) {
        uploadPromises.push(
          uploadFileWithPresignedUrl(client, selectedSampleFile)
        );
      } else {
        uploadPromises.push(Promise.resolve(editableData.sample_url));
      }

      const [finalThumbnailUrl, finalSampleUrl] = await Promise.all(
        uploadPromises
      );

      const UPDATE_BOOK_INFO = gql`
        mutation updateBookExhibitionByAdmin(
          $id: Int!
          $type: Type
          $is_view: Int
          $view_order: Int
          $title: String
          $description: String
          $object_url: String
          $sample_url: String
        ) {
          updateBookExhibitionByAdmin(
            id: $id
            type: $type
            is_view: $is_view
            view_order: $view_order
            title: $title
            description: $description
            object_url: $object_url
            sample_url: $sample_url
          ) {
            message
          }
        }
      `;

      const variables = {
        id: editableData.id,
        type: editableData.type,
        is_view: editableData.is_view,
        view_order: editableData.view_order,
        title: editableData.title,
        description: editableData.description,
        object_url: finalThumbnailUrl,
        sample_url: finalSampleUrl,
      };

      await client.mutate({
        mutation: UPDATE_BOOK_INFO,
        variables: variables,
      });

      alert("자료가 성공적으로 수정되었습니다.");
      refetchParentData();
      handleCloseClick();
    } catch (err) {
      alert(`수정 중 오류가 발생했습니다: ${err.message}`);
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalContainer>
      <h2>자료 정보 수정</h2>
      <form>
        <FormRow>
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              name="type"
              value={editableData.type || "SUPPORT"}
              label="Type"
              onChange={handleInputChange}
            >
              <MenuItem value={"SUPPORT"}>SUPPORT</MenuItem>
              <MenuItem value={"SAMPLE"}>SAMPLE</MenuItem>
            </Select>
          </FormControl>
        </FormRow>
        <FormRow>
          <TextField
            label="제목"
            name="title"
            value={editableData.title || ""}
            onChange={handleInputChange}
            fullWidth
          />
        </FormRow>
        <FormRow>
          <TextField
            label="설명"
            name="description"
            value={editableData.description || ""}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={3}
          />
        </FormRow>
        <FormRow>
          <FormControl fullWidth>
            <InputLabel>노출 여부</InputLabel>
            <Select
              name="is_view"
              value={editableData.is_view || 1}
              label="노출 여부"
              onChange={handleInputChange}
            >
              <MenuItem value={1}>노출</MenuItem>
              <MenuItem value={2}>비노출</MenuItem>
            </Select>
          </FormControl>
        </FormRow>
        <FormRow>
          <TextField
            label="노출 순서"
            name="view_order"
            type="number"
            value={editableData.view_order || ""}
            onChange={handleInputChange}
            fullWidth
          />
        </FormRow>

        <FormRow>
          <h4>썸네일</h4>
          {editableData.object_url && (
            <FilePreviewContainer>
              <img
                src={editableData.object_url}
                alt="Thumbnail preview"
                style={{ maxHeight: "150px", maxWidth: "100%" }}
              />
              <Button
                size="small"
                color="error"
                onClick={() => handleRemoveFile("object_url")}
              >
                썸네일 제거
              </Button>
            </FilePreviewContainer>
          )}
          <FileUploader
            selectedFiles={selectedFile ? [selectedFile] : []}
            setSelectedFiles={(files) => setSelectedFile(files[0] || null)}
          />
        </FormRow>

        <FormRow>
          <h4>샘플 파일</h4>
          {editableData.sample_url && (
            <FilePreviewContainer>
              <a
                href={editableData.sample_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                현재 샘플 파일 보기
              </a>
              <Button
                size="small"
                color="error"
                onClick={() => handleRemoveFile("sample_url")}
                sx={{ ml: 2 }}
              >
                샘플 파일 제거
              </Button>
            </FilePreviewContainer>
          )}
          <FileUploader
            selectedFiles={selectedSampleFile ? [selectedSampleFile] : []}
            setSelectedFiles={(files) =>
              setSelectedSampleFile(files[0] || null)
            }
          />
        </FormRow>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={handleCloseClick} sx={{ mr: 1 }}>
            취소
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={isLoading}>
            {isLoading ? "저장 중..." : "저장"}
          </Button>
        </Box>
      </form>
    </ModalContainer>
  );
};

export default BookModal;
